// SignatureFieldValue.js — structured signature field value helpers

import { normalizeDateValue } from 'utilities/DateTime';

function cleanString(value) {
  return String(value ?? '').trim();
}

/**
 * @param {unknown} value
 * @returns {object|null}
 */
export function normalizeSignatureFieldValue(value) {
  if (value == null || value === '') return null;
  if (typeof value !== 'object' || Array.isArray(value)) return null;

  const name = cleanString(value.name);
  const imageStoragePath = cleanString(value.imageStoragePath);
  const signedByKey = cleanString(value.signedByKey);
  const signedAtRaw = value.signedAt ?? null;
  const signedAtDate = normalizeDateValue(signedAtRaw);
  const signedAt = signedAtDate
    ? (signedAtRaw instanceof Date ? signedAtRaw.toISOString() : signedAtRaw)
    : null;

  if (!name && !imageStoragePath && !signedAt && !signedByKey) return null;

  return {
    name,
    signedAt,
    imageStoragePath,
    signedByKey,
  };
}

/**
 * @param {unknown} value
 * @returns {boolean}
 */
export function isSignatureFieldSigned(value) {
  const normalized = normalizeSignatureFieldValue(value);
  if (!normalized) return false;
  if (normalized.name) return true;
  if (normalized.imageStoragePath) return true;
  return Boolean(normalized.signedAt);
}

/**
 * Build a text-mode signature value.
 *
 * @param {object} options
 * @param {string} options.name
 * @param {string|null} [options.signedByKey]
 * @param {Date|string|null} [options.signedAt]
 * @returns {object|null}
 */
export function buildTextSignatureValue({
  name,
  signedByKey = null,
  signedAt = null,
} = {}) {
  const cleanedName = cleanString(name);
  if (!cleanedName) return null;

  const at = signedAt == null ? new Date() : signedAt;
  const date = normalizeDateValue(at) || new Date();
  const signedAtIso = date instanceof Date ? date.toISOString() : String(date);

  return normalizeSignatureFieldValue({
    name: cleanedName,
    signedAt: signedAtIso,
    imageStoragePath: '',
    signedByKey: cleanString(signedByKey) || '',
  });
}

/**
 * Build a written-mode signature value.
 *
 * @param {object} options
 * @param {string} options.name
 * @param {string} options.imageStoragePath
 * @param {string|null} [options.signedByKey]
 * @param {Date|string|null} [options.signedAt]
 * @returns {object|null}
 */
export function buildWrittenSignatureValue({
  name,
  imageStoragePath,
  signedByKey = null,
  signedAt = null,
} = {}) {
  const cleanedName = cleanString(name);
  const cleanedPath = cleanString(imageStoragePath);
  if (!cleanedName || !cleanedPath) return null;

  const at = signedAt == null ? new Date() : signedAt;
  const date = normalizeDateValue(at) || new Date();
  const signedAtIso = date instanceof Date ? date.toISOString() : String(date);

  return normalizeSignatureFieldValue({
    name: cleanedName,
    signedAt: signedAtIso,
    imageStoragePath: cleanedPath,
    signedByKey: cleanString(signedByKey) || '',
  });
}

/**
 * @param {unknown} value
 * @returns {string}
 */
export function formatSignatureFieldDisplay(value) {
  const normalized = normalizeSignatureFieldValue(value);
  if (!normalized) return '';
  if (normalized.name) return normalized.name;
  if (normalized.imageStoragePath) return 'Signed';
  return '';
}

/**
 * @param {unknown} signedAt
 * @param {string} [locale]
 * @returns {string}
 */
export function formatSignatureSignedAt(signedAt, locale = undefined) {
  const date = normalizeDateValue(signedAt);
  if (!date) return '';
  try {
    return date.toLocaleString(locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}
